<script setup lang="ts">
import { ref } from 'vue';
import { useModal } from '@/composables/useModal';
import {useUxData} from "@/stores/ux-data.ts";
// The template passes the DropDownList a null `rounded`, which drops its rounded corners; Kendo
// types the prop as a string, hence the cast there.
import {DropDownList} from "@progress/kendo-vue-dropdowns";
import {useCharacterDataStore} from "@/stores/character-data.ts";
import type {CharacterSheetInterface, SwadePerkPointAllocation} from "@/types/characters.ts";

const ux = useUxData();
const swade = useCharacterDataStore();
const character = defineModel<CharacterSheetInterface>('character')

const modalRef = ref<HTMLElement | null>(null);
const { show, hide } = useModal(modalRef);

defineExpose({ show, hide });
const allocationOptions = [
  {
    uuid: 'attributeIncrease',
    label: ux.labels.swade.tools.perkPointAllocator.attributeIncrease,
    value: 2,
  },
  {
    uuid: 'skillPoints',
    label: ux.labels.swade.tools.perkPointAllocator.skillPoints,
    value: 1,
  },
  {
    uuid: 'startingFunds',
    label: ux.labels.swade.tools.perkPointAllocator.startingFunds,
    value: 1,
  },
]

const onAllocationSelect = (event: any) => {
  console.debug("PerkPointAllocation | onAllocationSelect", event);
  if (!event.value || !character.value) return;

  const allocation = { ...event.value };

  swade.adjustPerkPointAllocation(character.value, allocation);
  swade.updateBuildValidation(character.value, swade.buildOptions);
  swade.updateBuildStatus(character.value);

  if (allocation.uuid === 'startingFunds') {
    swade.adjustStartingFundsPerk(character.value, 2);
  }
}

const onAllocationRemove = (allocation: SwadePerkPointAllocation) => {
  if (!character.value) return;

  character.value.protoData.perkPointAllocation = character.value.protoData?.perkPointAllocation?.filter((item: SwadePerkPointAllocation) => {
    return item !== allocation;
  });

  swade.updateBuildValidation(character.value, swade.buildOptions);
  swade.updateBuildStatus(character.value);

  if (allocation.uuid === 'startingFunds') {
    swade.adjustStartingFundsPerk(character.value, -2);
  }
}
</script>

<template>
<!-- Teleport to body so the modal escapes any parent stacking context (e.g. the
     fullscreen guided-setup modal). Bootstrap finds the modal by id via
     data-bs-target, so the trigger keeps working regardless of where the modal
     element lives in the DOM. -->
<Teleport to="body">
<div ref="modalRef" id="perk-point-allocator" class="modal fade" tabindex="-1">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header header perk-points">{{ ux.labels.swade.tools.perkPointAllocator.header }}</div>
      <div class="modal-body">
        <ul class="list-group list-group-flush">
          <li v-for="allocation in character?.protoData?.perkPointAllocation" class="list-group-item">
            <div class="d-flex flex-fill justify-content-between">
              <span class="allocation-label">{{ allocation.label }}</span>
              <span class="allocation-value ms-auto my-auto">{{ allocation.value }}</span>
              <span class="allocation-actions ms-2 my-auto">
                <button type="button" class="btn btn-sm btn-link p-0"
                        @click="onAllocationRemove(allocation)"
                >
                  <span class="fa-light fa-trash-alt"></span>
                </button>
              </span>
            </div>
          </li>
          <li v-if="!swade.build?.perkPoints?.fullyAllocated && !swade.build?.perkPoints?.overAllocated"
              class="list-group-item p-0">
            <DropDownList
              :data-items="allocationOptions"
              :rounded="(null as never)"
              text-field="label"
              @change="onAllocationSelect"
              :value="null"
            />
          </li>
        </ul>

        <small class="d-flex flex-fill flex-nowrap gap-2 mt-3">
          <span class="icon">
            <span class="fa-light fa-info-circle"></span>
          </span>
          <span class="my-auto" v-html="ux.labels?.swade?.tools?.perkPointAllocator?.allocationNote"></span>
        </small>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-link text-muted rounded-pill text-uppercase w-100"
                data-bs-dismiss="modal"
                data-bs-target="#perk-point-allocator"
        >
          {{ ux.labels.actions.done }}
        </button>
      </div>
    </div>
  </div>
</div>
</Teleport>
</template>
